import React from 'react';

export const CONSTANCIA_TIERS = [
  { key: 'constancia7', days: 7, label: 'Constancia I', emoji: '🏆' },
  { key: 'constancia30', days: 30, label: 'Constancia II', emoji: '🏆🏆' },
  { key: 'constancia100', days: 100, label: 'Constancia III', emoji: '🏆🏆🏆' },
  { key: 'constancia365', days: 365, label: 'Constancia IV', emoji: '🏆🏆🏆🏆' },
];

export function _constanciaAchievementsFromStreak(streak) {
  const out = {};
  const s = Number(streak || 0);

  for (const t of CONSTANCIA_TIERS) {
    out[t.key] = {
      unlocked: s >= t.days,
      progress: Math.min(s, t.days),
      goal: t.days,
      label: t.label,
      emoji: t.emoji,
    };
  }

  return out;
}

export function renderConstanciaRow(streak) {
  const s = Number(streak || 0);
  if (!Number.isFinite(s) || s <= 0) return null;

  const ach = _constanciaAchievementsFromStreak(s);

  const tiers = [
    ach.constancia7,
    ach.constancia30,
    ach.constancia100,
    ach.constancia365,
  ].filter(Boolean);

  if (!tiers.length) return null;

  let target = tiers.find(t => !t.unlocked);

  if (!target) target = tiers[tiers.length - 1];

  return (
    <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
      <span><b>{target.emoji} {target.label}</b></span>
      <span style={{fontWeight:800}}>
        {target.progress}/{target.goal}
      </span>
    </div>
  );
}

export function renderSabioRow(gami) {
  if (!gami) return null;

  const totalPts = Number(gami.xp || 0);

  const TIERS = [
    { min:0,max:9,label:'Aprendiz Cósmico',emoji:'🪶',nextAt:10 },
    { min:10,max:29,label:'Sabio',emoji:'✨',nextAt:30 },
    { min:30,max:59,label:'Maestro Estelar',emoji:'🔮',nextAt:60 },
    { min:60,max:99,label:'Oráculo Galáctico',emoji:'🌙',nextAt:100 },
    { min:100,max:149,label:'Iluminado Solar',emoji:'☀️',nextAt:150 },
    { min:150,max:Infinity,label:'Deidad Interior',emoji:'👁️',nextAt:null },
  ];

  let current = TIERS[TIERS.length-1];

  for (const t of TIERS) {
    if (totalPts >= t.min && totalPts <= t.max) {
      current = t;
      break;
    }
  }

  return (
    <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
      <span><b>{current.emoji} {current.label}</b></span>
      <span style={{fontWeight:800}}>
        {current.nextAt == null ? `${totalPts}+` : `${totalPts}/${current.nextAt}`}
      </span>
    </div>
  );
}

export function _computeGamificationFromStats(stats) {

  const totalScore = Number(stats?.totalScore ?? 0);
  const streak = Number(stats?.streak ?? 0);

  const thresholds = [0,10,25,45,70,100,135,175,220,270,330];

  const titles = [
    'Novato cósmico',
    'Explorador astral',
    'Guardián estelar',
    'Viajero galáctico',
    'Sabio celestial',
    'Maestro del cosmos',
    'Oráculo estelar',
    'Arquitecto del zodiaco',
    'Avatar del firmamento',
    'Leyenda del cosmos',
    'Deidad del zodíaco',
  ];

  let level = 0;

  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (totalScore >= thresholds[i]) {
      level = i;
      break;
    }
  }

  return {
    ok:true,
    xp:totalScore,
    level,
    levelTitle:titles[level],
    nextLevelAt:thresholds[Math.min(level+1,thresholds.length-1)],
    achievements:{
      ..._constanciaAchievementsFromStreak(streak),
      sabio30:{
        unlocked:totalScore>=30,
        progress:Math.min(totalScore,30),
        goal:30,
      },
      conexion12:{
        unlocked:false,
        progress:0,
        goal:12,
      },
    },
  };
}

export function _normalizeGamiForUI(serverGami, stats) {

  if (!serverGami || typeof serverGami !== 'object') {
    return _computeGamificationFromStats(stats);
  }

  const out = {
    ok:true,
    xp:Number(serverGami.xp ?? 0),
    level:Number(serverGami.level ?? 0),
    levelTitle:serverGami.levelTitle || 'Novato cósmico',
    nextLevelAt:Number(serverGami.nextLevelAt ?? 0),
    achievements:{
      ...(serverGami.achievements || {}),
    },
  };

  const localConst =
    _constanciaAchievementsFromStreak(stats?.streak || 0);

  Object.keys(localConst).forEach(k => {

    const srv = out.achievements[k] || {};

    out.achievements[k] = {
      ...srv,
      unlocked:Boolean(srv.unlocked) || localConst[k].unlocked,
      progress:Math.max(
        Number(srv.progress||0),
        Number(localConst[k].progress||0)
      ),
      goal:localConst[k].goal,
      label:localConst[k].label,
      emoji:localConst[k].emoji,
    };

  });

  return out;
}

export function _getMedalForStreak(streak) {

  const s = Number(streak || 0);

  if (s>=100)
    return {tier:'legend',label:'Leyenda',emoji:'👑',bg:'linear-gradient(135deg,#fff3b0,#ffd700)',border:'1px solid rgba(218,165,32,.85)',textColor:'#3a0c6e'};

  if (s>=60)
    return {tier:'champion',label:'Campeón',emoji:'🏆',bg:'linear-gradient(135deg,#ffeaa7,#ffd166)',border:'1px solid rgba(218,165,32,.7)',textColor:'#3a0c6e'};

  if (s>=30)
    return {tier:'elite',label:'Élite',emoji:'🏅',bg:'linear-gradient(135deg,#ffe9c0,#ffcd70)',border:'1px solid rgba(218,165,32,.6)',textColor:'#3a0c6e'};

  if (s>=14)
    return {tier:'gold',label:'Oro',emoji:'🥇',bg:'linear-gradient(135deg,#ffe082,#ffd54f)',border:'1px solid rgba(218,165,32,.55)',textColor:'#3a0c6e'};

  if (s>=7)
    return {tier:'silver',label:'Plata',emoji:'🥈',bg:'linear-gradient(135deg,#e0e0e0,#cfcfcf)',border:'1px solid rgba(120,120,120,.5)',textColor:'#2c1c4e'};

  if (s>=3)
    return {tier:'bronze',label:'Bronce',emoji:'🥉',bg:'linear-gradient(135deg,#e7b48a,#cd8f5b)',border:'1px solid rgba(160,110,70,.5)',textColor:'#2c1c4e'};

  return null;
}